import React, { Component, ErrorInfo, ReactNode } from "react";
import { ApiError } from "../api/client.ts";
import { analytics } from "../firebaseConfig.ts";
import { logEvent } from "firebase/analytics";

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  private errorCountTimer: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Increment error count to detect infinite loops
    this.setState((prevState) => ({
      errorCount: prevState.errorCount + 1,
    }));

    // Reset error count after 5 seconds
    if (this.errorCountTimer) {
      clearTimeout(this.errorCountTimer);
    }
    this.errorCountTimer = setTimeout(() => {
      this.setState({ errorCount: 0 });
    }, 5000);

    // If we've had more than 3 errors in 5 seconds, stop resetting
    if (this.state.errorCount >= 3) {
      console.error(
        "Error boundary detected infinite error loop",
        error,
        errorInfo,
      );
      return;
    }

    // For API errors, call onError callback (which shows toast) and reset
    if (error instanceof ApiError) {
      if (this.props.onError) {
        this.props.onError(error, errorInfo);
      }

      // Reset the error state after a short delay to allow toasts to display
      setTimeout(() => {
        this.setState({ hasError: false, error: null });
      }, 100);
    } else {
      // For non-API errors, log to console and show fallback UI
      console.error("Uncaught error:", error, errorInfo);

      // Log to Firebase Analytics for error tracking
      try {
        logEvent(analytics, "exception", {
          description: error.message || "Unknown error",
          fatal: true,
          error_name: error.name,
          component_stack: errorInfo.componentStack?.slice(0, 500), // Limit length
        });
      } catch (analyticsError) {
        // Silently fail if analytics logging fails
        console.error("Failed to log error to Firebase:", analyticsError);
      }
    }
  }

  componentWillUnmount() {
    if (this.errorCountTimer) {
      clearTimeout(this.errorCountTimer);
    }
  }

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    // Show fallback UI for non-API errors or if we've hit the error limit
    if (
      this.state.hasError &&
      (!(this.state.error instanceof ApiError) || this.state.errorCount >= 3)
    ) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-6">
              We encountered an unexpected error. Please refresh the page to
              continue.
            </p>
            <button
              onClick={this.handleRefresh}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Refresh Page
            </button>
            {process.env.NODE_ENV === "development" && this.state.error && (
              <div className="mt-6 text-left">
                <p className="text-xs text-gray-500 font-mono bg-gray-100 p-3 rounded overflow-auto">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // For API errors, let the toast handle it
    return this.props.children;
  }
}

export { ErrorBoundary };
